// istanbul ignore file
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { Storage, StorageConfig } from '@ionic/storage-angular';
import { Platform } from '@ionic/angular';
import * as Sentry from '@sentry/browser';
import { SecureStorageObject, SecureStoragePlugin } from './secure-storage-plugin';
import { SentryService, SentrySeverityLevel } from '../sentry/sentry.service';

export interface SecureStorageConfig {
  name?: string;
  securePrompt?: boolean;
  forceSecure?: boolean;
}

export abstract class SecureDeviceDelegator {
  abstract onUnsecured(secure: () => Promise<void>): Promise<void>;
}

class NoopDelegator extends SecureDeviceDelegator {
  async onUnsecured() {
    console.warn('SecureDeviceDelegator is not implemented');
    throw new Error('SecureDeviceDelegator is not implemented');
  }
}

export const LegacyStorageConfigToken = new InjectionToken<StorageConfig>('LEGACY_STORAGE_CONFIG_TOKEN');

export const SecureStorageConfigToken = new InjectionToken<SecureStorageConfig>('SECURE_STORAGE_CONFIG_TOKEN');

const TAG = 'StorageService';

@Injectable()
export class StorageLegacyService {
  type: 'ionic' | 'secure';
  ionicStorage: Storage;
  secureStorageObject: SecureStorageObject;

  constructor(
    @Inject(SecureStorageConfigToken) private secureStorageConfig: SecureStorageConfig,
    @Inject(LegacyStorageConfigToken) private storageConfig: StorageConfig,
    private secureStorage: SecureStoragePlugin,
    private platform: Platform,
    private sentryService: SentryService,
    @Optional() private secureDeviceDelegator: SecureDeviceDelegator
  ) {
    if (!(this.secureDeviceDelegator instanceof SecureDeviceDelegator)) {
      this.secureDeviceDelegator = new NoopDelegator();
    }
  }

  async init() {
    await this.platform.ready();

    // use ionic storage on browser
    if (!this.platform.is('cordova')) {
      this.type = 'ionic';
      this.ionicStorage = new Storage(this.storageConfig);
      await this.ionicStorage.create();
      return;
    }

    await this.initSecureStorage();
  }

  private async initSecureStorage(): Promise<void> {
    try {
      this.secureStorageObject = await this.secureStorage.create(this.secureStorageConfig.name);
      this.type = 'secure';
    } catch (error) {
      this.type = 'ionic'; // always fallback to ionic
      this.ionicStorage = new Storage(this.storageConfig);
      await this.ionicStorage.create();

      /* istanbul ignore else */
      if (error instanceof Error) {
        /* istanbul ignore else */
        if (error.message.startsWith('Device is not secure')) {
          this.sentryService.setMessageForSentryWithErrorInfo(
            `[${TAG}#initSecureStorage] Device is not secure, fallback to ionic storage`,
            SentrySeverityLevel.Warning,
            { error }
          );
          return this.secureDevice(() => this.initSecureStorage());
        }
      }
      this.sentryService.setMessageForSentry(`${TAG}#initSecureStorage`, SentrySeverityLevel.Error);
    }
  }

  private async secureDevice<T>(callback: () => Promise<T>): Promise<T | void> {
    if (!this.secureStorageConfig.securePrompt) {
      return;
    }

    try {
      await this.secureDeviceDelegator.onUnsecured(() => this.secureStorage.secureDevice());
      return callback();
    } catch (error) {
      const forceSecure = !!this.secureStorageConfig.forceSecure;

      this.sentryService.setMessageForSentryWithErrorInfo(`[${TAG}#secureDevice] force secure device`, SentrySeverityLevel.Warning, {
        error,
        forceSecure,
      });

      if (forceSecure) {
        return this.secureDevice(callback);
      }
    }
  }

  /**
   * Get the data
   *
   * @param key the key to identify this value
   * @returns Returns a promise that resolves when the key and value are set. If value not found will return null.
   */
  async get<T = any>(key: string): Promise<T | null> {
    try {
      const data = this.type === 'secure' ? await this.getSecure(key) : await this.ionicStorage.get(key);
      return data ? data : null;
    } catch (error) {
      /* istanbul ignore else */
      if (error instanceof Error) {
        /* istanbul ignore else */
        if (error.message.startsWith("KeyStore doesn't contain alias")) {
          this.sentryService.setMessageForSentryWithErrorInfo(
            `[${TAG}#get] Signature not matched, resetting storage`,
            SentrySeverityLevel.Warning,
            { error }
          );
          return this.reset(() => this.get(key));
        }
      }
      this.sentryService.setMessageForSentryWithErrorInfo(`${TAG}#get`, SentrySeverityLevel.Error, { error });
      throw error;
    }
  }

  private async getSecure(key: string) {
    if (await this.secureHasKey(key)) {
      return JSON.parse(await this.secureStorageObject.get(key));
    }
    return null;
  }

  private async secureHasKey(key: string) {
    const keys = await this.secureStorageObject.keys();

    if (!keys.includes(key)) return false;

    return true;
  }

  /**
   * Set the value for the given key.
   *
   * @param key the key to identify this value
   * @param data the value for this key
   * @returns Returns a promise that resolves when the key and value are set
   */
  async set<T>(key: string, data: T): Promise<void> {
    try {
      if (this.type === 'secure') {
        const json = JSON.stringify(data);
        await this.secureStorageObject.set(key, json);
      } else {
        await this.ionicStorage.set(key, data);
      }
    } catch (error) {
      /* istanbul ignore else */
      if (error instanceof Error) {
        /* istanbul ignore else */
        if (error.message.startsWith("KeyStore doesn't contain alias")) {
          this.sentryService.setMessageForSentryWithErrorInfo(
            `[${TAG}#set] Signature not matched, resetting storage`,
            SentrySeverityLevel.Warning,
            { error }
          );

          return this.reset(() => this.set(key, data));
        }
      }
      this.sentryService.setMessageForSentryWithErrorInfo(`${TAG}#set`, SentrySeverityLevel.Error, { error });

      throw error;
    }
  }

  /**
   * Remove any value associated with this key.
   *
   * @param key the key to identify this value
   * @returns Returns a promise that resolves when the value is removed
   */
  async remove(key: string): Promise<void> {
    try {
      if (this.type === 'secure') {
        if (await this.secureHasKey(key)) {
          await this.secureStorageObject.remove(key);
        }
      } else {
        await this.ionicStorage.remove(key);
      }
    } catch (error) {
      this.sentryService.setMessageForSentryWithErrorInfo(`${TAG}#remove`, SentrySeverityLevel.Error, { error });

      throw error;
    }
  }

  async keys(): Promise<string[]> {
    try {
      if (this.type === 'secure') {
        return await this.secureStorageObject.keys();
      } else {
        return await this.ionicStorage.keys();
      }
    } catch (error) {
      this.sentryService.setMessageForSentryWithErrorInfo(`${TAG}#keys`, SentrySeverityLevel.Error, { error });

      throw error;
    }
  }

  private async reset<T>(callback: () => Promise<T>): Promise<T> {
    await this.secureStorageObject.clear();
    return callback();
  }
}
